import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, CheckCircle, Ban } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Button } from '../ui/button';
import { useClinic } from '@/contexts/ClinicContext';
import { roleService } from '@/services/roleService';

export interface Role {
  id: string;
  name: string;
  description: string;
  permissions: {
    [module: string]: string[];
  };
  isActive: boolean;
  userCount: number;
  createdAt: string;
  updatedAt: string;
}

interface DeactivateRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: Role | null;
  onSuccess: (role: Role) => void;
}

const DeactivateRoleDialog: React.FC<DeactivateRoleDialogProps> = ({
  open,
  onOpenChange,
  role,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { clinic } = useClinic();

  if (!role) return null;

  const isActive = role.isActive;
  const actionText = isActive ? 'Deactivate' : 'Activate';
  const actionDescription = isActive
    ? 'This will deactivate the role. It will no longer be able to assign to users, but its data will be preserved.'
    : 'This will activate the role and make it available for assignment to users.';

  const handleConfirm = async () => {
    try {
      setIsLoading(true);
      await roleService.toggleClinicRoleStatus(clinic?.id, role.id || '');

      toast({
        title: isActive ? 'Role Deactivated' : 'Role Activated',
        description: `Role has been ${isActive ? 'deactivated' : 'activated'} successfully.`,
        variant: 'default',
      });

      onSuccess(role);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to toggle role status',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
              isActive ? 'bg-red-50' : 'bg-brand-teal/10'
            }`}>
              {isActive ? (
                <Ban className="h-6 w-6 text-red-500" />
              ) : (
                <CheckCircle className="h-6 w-6 text-brand-teal" />
              )}
            </div>
            <div className="flex-1">
              <DialogTitle className="text-[1.05rem] font-semibold">
                {actionText} Role
              </DialogTitle>
              <DialogDescription className="text-[0.8rem] text-slate-500 mt-0">
                Are you sure you want to {actionText.toLowerCase()} this role?
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-2">
          <p className="text-sm text-slate-600">
            Are you sure you want to {actionText.toLowerCase()} <span className="font-semibold text-slate-900">{role.name}</span>? {actionDescription}
          </p>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className={isActive 
              ? 'bg-red-500 hover:bg-red-600' 
              : 'bg-brand-teal hover:bg-brand-teal/90'
            }
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                {isActive ? 'Deactivating...' : 'Activating...'}
              </>
            ) : (
              <>
                {isActive ? <Ban className="h-4 w-4 mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                {actionText}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeactivateRoleDialog;
